import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ModuleLink as Link } from '../../components/ModuleLink';
import Layout from '../../components/Layout';
import StepIndicator from '../../components/StepIndicator';
import { useAuth } from '../../context/AuthContext';
import { useApiClient } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import { sectionMap, AMENDABLE_STATUSES } from '../../utils';
import type { ChangeRequest } from '../../types';

import NotifySection         from './sections/NotifySection';
import ResponsesSection      from './sections/ResponsesSection';
import PlanSection           from './sections/PlanSection';
import SignoffSection        from './sections/SignoffSection';
import ApprovalSection       from './sections/ApprovalSection';
import ImplementationSection from './sections/ImplementationSection';
import ReviewSection         from './sections/ReviewSection';
import RejectionSection      from './sections/RejectionSection';
import ClosedSection         from './sections/ClosedSection';

export default function ManageChange() {
  const [searchParams]         = useSearchParams();
  const cid                    = searchParams.get('cid') ?? '';
  const { userEmail }          = useAuth();
  const api                    = useApiClient();
  useToast();
  const [record, setRecord]    = useState<ChangeRequest | null>(null);
  const [loading, setLoading]  = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [notFound, setNotFound] = useState(false);

  async function fetchRecord() {
    try {
      const data = await api.get<ChangeRequest>(`/changes/${encodeURIComponent(cid)}`);
      if (!data) { setNotFound(true); return; }
      if (data.leadEmail.toLowerCase() !== userEmail.toLowerCase()) { setAccessDenied(true); return; }
      setRecord(data);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { if (cid) fetchRecord(); else setNotFound(true); }, [cid]);

  if (!cid || notFound) return (
    <Layout>
      <div className="card">
        <div className="state-card">
          <div className="state-card__icon"><i className="fa-solid fa-circle-question" aria-hidden="true" /></div>
          <h2 className="state-card__title">Change Not Found</h2>
          <p className="state-card__body">The change record could not be found.</p>
          <Link to="/" className="btn btn--secondary" style={{ marginTop: 8 }}>Back to My Changes</Link>
        </div>
      </div>
    </Layout>
  );

  if (accessDenied) return (
    <Layout>
      <div className="card">
        <div className="state-card">
          <div className="state-card__icon"><i className="fa-solid fa-lock" aria-hidden="true" /></div>
          <h2 className="state-card__title">Access Denied</h2>
          <p className="state-card__body">Only the assigned Change Lead can manage this change.</p>
          <Link to="/" className="btn btn--secondary" style={{ marginTop: 8 }}>Back to My Changes</Link>
        </div>
      </div>
    </Layout>
  );

  if (loading || !record) return (
    <Layout>
      <div className="loading-state">
        <div className="spinner spinner--lg" role="status" aria-label="Loading" />
        <span>Loading change record…</span>
      </div>
    </Layout>
  );

  const section = sectionMap[record.status] ?? 'notify';

  const sectionProps = { record, cid, onRefresh: fetchRecord };

  return (
    <Layout>
      <div className="page-header">
        <div className="page-header__title">
          <div className="breadcrumb">
            <Link to="/" className="breadcrumb__link"><i className="fa-solid fa-house" aria-hidden="true" /> Home</Link>
            <span className="breadcrumb__sep">/</span>
            <span className="breadcrumb__current">{record.changeId}</span>
          </div>
          <h1>{record.changeName}</h1>
          <p className="text-muted">{record.changeId} · {record.scope} · {record.status}</p>
        </div>
        <div className="page-header__actions">
          {AMENDABLE_STATUSES.includes(record.status) && (
            <Link to={`/amend-change?cid=${encodeURIComponent(cid)}`} className="btn btn--secondary btn--sm">
              <i className="fa-solid fa-pen-to-square" aria-hidden="true" /> Edit submission
            </Link>
          )}
          <Link to={`/change-detail?cid=${encodeURIComponent(cid)}`} className="btn btn--secondary btn--sm">
            <i className="fa-solid fa-eye" aria-hidden="true" /> View Full Record
          </Link>
        </div>
      </div>

      <StepIndicator currentStep={record.currentStep} />

      {section === 'notify'         && <NotifySection         {...sectionProps} />}
      {section === 'responses'      && <ResponsesSection      {...sectionProps} />}
      {section === 'plan'           && <PlanSection           {...sectionProps} />}
      {section === 'signoff'        && <SignoffSection         {...sectionProps} />}
      {section === 'approval'       && <ApprovalSection       {...sectionProps} />}
      {section === 'implementation' && <ImplementationSection {...sectionProps} />}
      {section === 'review'         && <ReviewSection         {...sectionProps} />}
      {section === 'rejection'      && <RejectionSection      {...sectionProps} />}
      {section === 'closed'         && <ClosedSection         {...sectionProps} />}
    </Layout>
  );
}
